import { BitField, type BitSet } from "easy-bits";
import { useEffect, useRef, useState } from "react";
import { ButtonGroup, Col, Form, Row, ToggleButton } from "react-bootstrap";
import { ExclamationCircleFill } from "react-bootstrap-icons";
import { Typeahead, type TypeaheadRef } from "react-bootstrap-typeahead";
import type { Option } from "react-bootstrap-typeahead/types/types";
import {
  type Case,
  type Client,
  type ExternalPartners,
  PropoundingParty,
  Respondent,
} from "@/briefpoint-client";
import { useAuth } from "@/hooks/useAuth";
import SwapIcon from "@/images/swap_vert.svg?react";
import {
  FamilyLawCaseType,
  PETITIONER_AND_RESPONDENT_TYPES,
} from "@/lib/constants";
import type {
  CaseResponse,
  ClientResponse,
  CombinedOption,
  ExternalClientResponse,
} from "@/lib/types";
import {
  type CombinedClientSearch,
  mergeClients,
} from "@/utils/ConfirmInfo/utils";
import styles from "./ReviewPage.module.scss";
import { CustomMenuClient } from "./utils";

export const RespondentDisplay: { [key in Respondent]: string } = {
  [Respondent.Respondent]: "Respondent",
  [Respondent.PlaintiffOrDefendant]: "Plaintiff/Defendant",
  [Respondent.Custom]: "Custom",
  [Respondent.RespondingParty]: "Responding Party",
};

// Parties
function ReviewPageThree({
  areThereFuzzyClientResults,
  propoundingParty,
  setPropoundingParty,
  respondingParty,
  setRespondingParty,
  propoundingPartyName,
  setPropoundingPartyName,
  respondingPartyName,
  setRespondingPartyName,
  isCrossComplaint,
  client,
  clients,
  externalClients,
  _case,
  respondent,
  setRespondent,
  respondentCustom,
  setCase,
  setClient,
  setRespondentCustom,
}: {
  areThereFuzzyClientResults: boolean;
  propoundingParty: PropoundingParty | undefined;
  setPropoundingParty: React.Dispatch<
    React.SetStateAction<PropoundingParty | undefined>
  >;
  respondingParty: PropoundingParty | undefined;
  setRespondingParty: React.Dispatch<
    React.SetStateAction<PropoundingParty | undefined>
  >;
  propoundingPartyName: string;
  setPropoundingPartyName: React.Dispatch<React.SetStateAction<string>>;
  respondingPartyName: string;
  setRespondingPartyName: React.Dispatch<React.SetStateAction<string>>;
  isCrossComplaint: boolean;
  _case?: CaseResponse;
  client?: ClientResponse;
  clients?: ClientResponse[];
  externalClients?: ExternalClientResponse[];
  respondent: Respondent;
  setRespondent: (respondent: Respondent) => void;
  respondentCustom: string;
  setCase: React.Dispatch<React.SetStateAction<Case | undefined>>;
  setClient: React.Dispatch<React.SetStateAction<Client | undefined>>;
  setRespondentCustom: React.Dispatch<React.SetStateAction<string>>;
}) {
  const { user } = useAuth();

  const isCurClientMatch: boolean =
    _case?.clientId !== undefined &&
    client?.id !== undefined &&
    _case?.clientId === client?.id;

  // if the current client matches, default to the 'find client' field
  const [clientAction, setClientAction] = useState<string>(
    isCurClientMatch || (!_case?.clientId && client?.id) ? "2" : "1",
  );
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const clientTypeaheadRef = useRef<TypeaheadRef>(null);

  const clientActionTypes = [
    { name: "New Client", value: "1" },
    { name: "Existing Client", value: "2" },
  ];

  const respondents = [
    Respondent.Respondent,
    Respondent.RespondingParty,
    Respondent.PlaintiffOrDefendant,
    Respondent.Custom,
  ];

  const isFamilyLawCase =
    _case?.usePetitionerAndRespondent ?? _case?.caseType === FamilyLawCaseType;

  const propoundingPartyConfig: BitSet<PropoundingParty> =
    new BitField<PropoundingParty>();
  propoundingPartyConfig.on(propoundingParty || PropoundingParty.Unknown);
  const respondingPartyConfig: BitSet<PropoundingParty> =
    new BitField<PropoundingParty>();
  if (respondingParty === undefined) {
    if (propoundingPartyConfig.test(PropoundingParty.Plaintiff)) {
      respondingPartyConfig.on(PropoundingParty.Defendant);
      if (isCrossComplaint ?? false) {
        respondingPartyConfig.on(PropoundingParty.CrossComplainant);
      }
    } else {
      respondingPartyConfig.on(PropoundingParty.Plaintiff);
      if (isCrossComplaint) {
        respondingPartyConfig.on(PropoundingParty.CrossDefendant);
      }
    }
  } else {
    respondingPartyConfig.on(respondingParty);
  }

  const propoundingPartyNameMap = new Map([
    [
      PropoundingParty.Plaintiff,
      isFamilyLawCase
        ? PETITIONER_AND_RESPONDENT_TYPES.PETITIONER
        : PETITIONER_AND_RESPONDENT_TYPES.PLAINTIFF,
    ],
    [
      PropoundingParty.Defendant,
      isFamilyLawCase
        ? PETITIONER_AND_RESPONDENT_TYPES.RESPONDENT
        : PETITIONER_AND_RESPONDENT_TYPES.DEFENDANT,
    ],
    [
      PropoundingParty.CrossComplainant,
      isFamilyLawCase
        ? PETITIONER_AND_RESPONDENT_TYPES.CROSS_COMPLAINANT
        : PETITIONER_AND_RESPONDENT_TYPES.CROSS_PETITIONER,
    ],
    [
      PropoundingParty.CrossDefendant,
      isFamilyLawCase
        ? PETITIONER_AND_RESPONDENT_TYPES.CROSS_RESPONDENT
        : PETITIONER_AND_RESPONDENT_TYPES.CROSS_DEFENDANT,
    ],
  ]);

  function handleClientChange(e: Option[]): void {
    const val = e[0];

    if (val) {
      const clientVal = val as CombinedClientSearch;
      let _client: Client | undefined = clients?.find(
        (x) => x.name === clientVal.name,
      );

      if (!_client) {
        const integration = clientVal.partner
          ? { partner: clientVal.partner, identifier: clientVal.externalId }
          : undefined;
        _client = { name: clientVal.name, integration };
      }

      setClient(_client);

      setRespondingPartyName(clientVal.name);
      setShowMenu(false);
      if (!!clientVal && !!clientVal?.internalId) {
        setShowMenu(false);
        setClientAction("2");
      } else {
        setClientAction("1");
      }
    }
  }

  function handleToggleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.currentTarget.value;
    setClientAction(value);
    setClient(undefined);
    clientTypeaheadRef.current?.focus();
    if (value === "1") {
      if (clients) {
        clientTypeaheadRef.current?.toggleMenu();
      }
    }
  }

  function handleOnInputChange(text: string) {
    if (text) {
      setClient({ name: text });
      setRespondingPartyName(text);
      setClientAction("1");
      setShowMenu(true);
    } else {
      setClient(undefined);
      setShowMenu(false);
    }
  }

  function handlePetitionerSelectionChange(
    e: React.ChangeEvent<HTMLInputElement>,
  ) {
    const checked = e.currentTarget.checked;
    setCase((prev) => ({
      ...prev,
      usePetitionerAndRespondent: checked,
    }));
  }

  function getRespondingPartyType() {
    for (const [key, value] of propoundingPartyNameMap) {
      if (respondingPartyConfig.test(key)) {
        return value;
      }
    }
    return "";
  }

  const respondingPartyType = getRespondingPartyType();

  function updatePropoundingParty(value: PropoundingParty) {
    propoundingPartyConfig.flip(value);
    setPropoundingParty(propoundingPartyConfig.valueOf() as PropoundingParty);
  }

  function updateRespondingParty(value: PropoundingParty) {
    respondingPartyConfig.flip(value);
    setRespondingParty(respondingPartyConfig.valueOf() as PropoundingParty);
  }

  const sortedClients: CombinedClientSearch[] = mergeClients(
    clients,
    externalClients,
  ).sort((a, b) => {
    const aHasPartnerNoId = a.partner && !a.internalId;
    const bHasPartnerNoId = b.partner && !b.internalId;

    if (aHasPartnerNoId && !bHasPartnerNoId) {
      return -1;
    }
    if (!aHasPartnerNoId && bHasPartnerNoId) {
      return 1;
    }

    if (a.partner && !b.partner) {
      return -1;
    }
    if (!a.partner && b.partner) {
      return 1;
    }

    const caseIdA = a.internalId || "";
    const caseIdB = b.internalId || "";
    if (!caseIdA && caseIdB) {
      return -1;
    }
    if (caseIdA && !caseIdB) {
      return 1;
    }

    const shortTitleA = a.name || "";
    const shortTitleB = b.name || "";

    // If a fuzzy search result exists, preserve the order provided by the fuzzy search to prioritize
    // client selection list relative to the fuzzy search result, otherwise, sort the BP clients by name
    if (!areThereFuzzyClientResults) {
      return shortTitleA.localeCompare(shortTitleB);
    }
    return 0;
  });

  function swapParties() {
    setClient((prev) => {
      if (!prev) return;

      // This check is to ensure that if we swap back to a respondingName that matches an existing client, we take the integration
      // info that matches with it so that the logo in the typeahead accurately reflects whether the client is external/internal
      const existingClient = sortedClients?.find(
        (x) => x.name === propoundingPartyName,
      );
      let integration:
        | { partner: ExternalPartners; identifier: string }
        | undefined;

      if (existingClient) {
        integration =
          existingClient.externalId && existingClient.partner
            ? {
                partner: existingClient.partner,
                identifier: existingClient.externalId,
              }
            : undefined;
      }

      return {
        ...prev,
        name: propoundingPartyName,
        ...(integration ? { integration } : { integration: undefined }),
      };
    });
    setRespondingPartyName(propoundingPartyName);
    setPropoundingPartyName(respondingPartyName);
    setPropoundingParty(respondingParty);
    setRespondingParty(propoundingParty);
  }

  const getExternalPartnerStyle = () => {
    if (client?.integration?.partner) {
      return styles[`external-partner-${client.integration.partner}`];
    }

    return styles["external-partner-none"];
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore setRespondingPartyName
  useEffect(() => {
    /* if and when client is set, set the responding party name to the client name */
    if (client) {
      setRespondingPartyName(client?.name ?? "");
    }
  }, [client]);

  return (
    <div>
      <div className="mb-2">
        <Form.Group>
          <Row>
            <Col xs="12">
              <Form.Check
                id={"propounding-petitioner-selection"}
                type={"checkbox"}
                label={
                  PETITIONER_AND_RESPONDENT_TYPES.PETITIONER_AND_RESPONDENT
                }
                checked={isFamilyLawCase ?? false}
                onChange={(e) => handlePetitionerSelectionChange(e)}
                className="mb-2"
              />
            </Col>
          </Row>
        </Form.Group>
        <Form.Group>
          <label htmlFor="propounding-party-name">Propounding Party</label>
          <Form.Control
            required
            className="mb-2"
            size="sm"
            value={propoundingPartyName}
            onChange={(event) =>
              setPropoundingPartyName(event.currentTarget.value)
            }
          />
        </Form.Group>
        <Form.Group>
          <Row>
            <Col xs="6">
              <Form.Check
                id={"propounding-party-plaintiff"}
                type={"checkbox"}
                label={
                  isFamilyLawCase
                    ? PETITIONER_AND_RESPONDENT_TYPES.PETITIONER
                    : PETITIONER_AND_RESPONDENT_TYPES.PLAINTIFF
                }
                checked={propoundingPartyConfig.test(
                  PropoundingParty.Plaintiff,
                )}
                onChange={() =>
                  updatePropoundingParty(PropoundingParty.Plaintiff)
                }
                isInvalid={propoundingParty === PropoundingParty.Unknown}
              />
            </Col>
            <Col xs="6">
              <Form.Check
                id={"propounding-party-cross-defendant"}
                type={"checkbox"}
                label={
                  isFamilyLawCase
                    ? PETITIONER_AND_RESPONDENT_TYPES.CROSS_RESPONDENT
                    : PETITIONER_AND_RESPONDENT_TYPES.CROSS_DEFENDANT
                }
                checked={propoundingPartyConfig.test(
                  PropoundingParty.CrossDefendant,
                )}
                onChange={() =>
                  updatePropoundingParty(PropoundingParty.CrossDefendant)
                }
                isInvalid={propoundingParty === PropoundingParty.Unknown}
              />
            </Col>
          </Row>
          <Row>
            <Col xs="6">
              <Form.Check
                id={"propounding-party-defendant"}
                type={"checkbox"}
                label={
                  isFamilyLawCase
                    ? PETITIONER_AND_RESPONDENT_TYPES.RESPONDENT
                    : PETITIONER_AND_RESPONDENT_TYPES.DEFENDANT
                }
                checked={propoundingPartyConfig.test(
                  PropoundingParty.Defendant,
                )}
                onChange={() =>
                  updatePropoundingParty(PropoundingParty.Defendant)
                }
                isInvalid={propoundingParty === PropoundingParty.Unknown}
              />
            </Col>
            <Col xs="6">
              <Form.Check
                id={"propounding-party-cross-complainant"}
                type={"checkbox"}
                label={
                  isFamilyLawCase
                    ? PETITIONER_AND_RESPONDENT_TYPES.CROSS_PETITIONER
                    : PETITIONER_AND_RESPONDENT_TYPES.CROSS_COMPLAINANT
                }
                checked={propoundingPartyConfig.test(
                  PropoundingParty.CrossComplainant,
                )}
                onChange={() =>
                  updatePropoundingParty(PropoundingParty.CrossComplainant)
                }
                isInvalid={propoundingParty === PropoundingParty.Unknown}
              />
            </Col>
          </Row>
        </Form.Group>
      </div>
      <Form.Group>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <div style={{ flex: 1, height: "1px", backgroundColor: "#d2d5d5" }} />
          {!isCurClientMatch && (
            <div>
              <SwapIcon
                color="#4d5758"
                title="Swap Parties"
                style={{
                  cursor: "pointer",
                  width: "24px",
                  height: "24px",
                  textAlign: "center",
                }}
                onClick={swapParties}
              />
            </div>
          )}
          <div style={{ flex: 1, height: "1px", backgroundColor: "#d2d5d5" }} />
        </div>
        {!isCurClientMatch && (
          <div className={styles.caseSwitchToggle}>
            <ButtonGroup className={styles.bpToggleTxtSwitch}>
              {clientActionTypes.map((type, idx) => (
                <ToggleButton
                  key={idx}
                  id={`radio-${idx}`}
                  className={
                    clientAction === type.value ? styles.btnChecked : ""
                  }
                  type="radio"
                  name="radio"
                  value={type.value}
                  checked={clientAction === type.value}
                  onChange={handleToggleChange}
                >
                  {type.name}
                </ToggleButton>
              ))}
            </ButtonGroup>
          </div>
        )}
        <div className={styles.checkMatchContainer}>
          {!isCurClientMatch && (
            <>
              <ExclamationCircleFill />{" "}
              <p>This client doesn't match an existing client</p>
            </>
          )}
        </div>
        <Form.Group className="mb-2">
          <label htmlFor="responding-party-client">
            Responding Party (Client)
          </label>
          <Typeahead
            className={getExternalPartnerStyle()}
            onChange={(e) => handleClientChange(e)}
            onInputChange={handleOnInputChange}
            selected={(client && [client]) || []}
            clearButton
            isInvalid={!client}
            id="client"
            size="sm"
            emptyLabel={
              clientAction === "1" &&
              "No matches found, this will create a new client."
            }
            options={sortedClients || []}
            // Disable the menu on click when toggle is switch to 'new client' to prevent initial menu pop up
            onFocus={
              clientAction === "1"
                ? () => setShowMenu(false)
                : () => setShowMenu(true)
            }
            onBlur={() => {
              setShowMenu(false);
            }}
            open={showMenu}
            labelKey="name"
            flip={false}
            placeholder={
              clientAction === "1"
                ? "Create a new client..."
                : "Find an existing client..."
            }
            renderMenu={(results, menuProps, state) => (
              <CustomMenuClient
                results={results as CombinedOption[]}
                menuProps={menuProps}
                state={state}
                showSourceLogos={!!user?.externalConnection?.isActive}
                isClientNew={clientAction === "1"}
              />
            )}
            ref={clientTypeaheadRef}
          />
        </Form.Group>
        <div className="mb-2">
          <Row>
            <Col xs="6">
              <Form.Check
                id={"responding-party-plaintiff"}
                type={"checkbox"}
                label={
                  isFamilyLawCase
                    ? PETITIONER_AND_RESPONDENT_TYPES.PETITIONER
                    : PETITIONER_AND_RESPONDENT_TYPES.PLAINTIFF
                }
                checked={respondingPartyConfig.test(PropoundingParty.Plaintiff)}
                onChange={() =>
                  updateRespondingParty(PropoundingParty.Plaintiff)
                }
                isInvalid={respondingParty === PropoundingParty.Unknown}
              />
            </Col>
            <Col xs="6">
              <Form.Check
                id={"responding-party-cross-defendant"}
                type={"checkbox"}
                label={
                  isFamilyLawCase
                    ? PETITIONER_AND_RESPONDENT_TYPES.CROSS_RESPONDENT
                    : PETITIONER_AND_RESPONDENT_TYPES.CROSS_DEFENDANT
                }
                checked={respondingPartyConfig.test(
                  PropoundingParty.CrossDefendant,
                )}
                onChange={() =>
                  updateRespondingParty(PropoundingParty.CrossDefendant)
                }
                isInvalid={respondingParty === PropoundingParty.Unknown}
              />
            </Col>
          </Row>
          <Row>
            <Col xs="6">
              <Form.Check
                id={"responding-party-defendant"}
                type={"checkbox"}
                label={
                  isFamilyLawCase
                    ? PETITIONER_AND_RESPONDENT_TYPES.RESPONDENT
                    : PETITIONER_AND_RESPONDENT_TYPES.DEFENDANT
                }
                checked={respondingPartyConfig.test(PropoundingParty.Defendant)}
                onChange={() =>
                  updateRespondingParty(PropoundingParty.Defendant)
                }
                isInvalid={respondingParty === PropoundingParty.Unknown}
              />
            </Col>
            <Col xs="6">
              <Form.Check
                id={"responding-party-cross-complainant"}
                type={"checkbox"}
                label={
                  isFamilyLawCase
                    ? PETITIONER_AND_RESPONDENT_TYPES.CROSS_PETITIONER
                    : PETITIONER_AND_RESPONDENT_TYPES.CROSS_COMPLAINANT
                }
                checked={respondingPartyConfig.test(
                  PropoundingParty.CrossComplainant,
                )}
                onChange={() =>
                  updateRespondingParty(PropoundingParty.CrossComplainant)
                }
                isInvalid={respondingParty === PropoundingParty.Unknown}
              />
            </Col>
          </Row>
        </div>
      </Form.Group>
      <div>
        <Form.Group>
          <label htmlFor="respondent">Refer to responding party as</label>
          <Form.Select
            required
            value={respondent}
            size="sm"
            onChange={(event) =>
              setRespondent(Number(event.currentTarget.value))
            }
          >
            {respondents.map((respondent, index) => (
              <option key={index} value={respondent}>
                {respondent === Respondent.PlaintiffOrDefendant
                  ? respondingPartyType
                  : RespondentDisplay[respondent]}
              </option>
            ))}
          </Form.Select>
          {respondent === Respondent.Custom && (
            <Form.Control
              required
              placeholder="Enter custom name"
              className="mt-2"
              size="sm"
              defaultValue={respondentCustom}
              onChange={(event) =>
                setRespondentCustom(event.currentTarget.value)
              }
            />
          )}
        </Form.Group>
      </div>
    </div>
  );
}

export default ReviewPageThree;
